/** The search syntax as a short guide under the search box; an example inserts itself into the box. */
import { useEffect, useMemo, useState } from 'react';
import { buildTips, pickSample, SEARCH_TIPS_NOTE, type TipSample } from '../lib/searchtips.ts';
import { Api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { Kicker } from '../design/primitives.tsx';

const cache = new Map<string, TipSample | null>();

/** A few of the newest documents of the tenant; the examples are made of the most useful one. */
async function loadSample(tid: string): Promise<TipSample | null> {
  const found = await Api.search(tid, new URLSearchParams({ q: '', size: '6', from: '2000-01-01T00:00:00Z' }));
  const docs = await Promise.all(found.hits.slice(0, 4).map((h) => Api.message(tid, h.vaultId, h.uuid, h.day).catch(() => undefined)));
  const samples: TipSample[] = docs.filter((d): d is NonNullable<typeof d> => !!d).map((d) => ({ fields: d.fields, meta: d.meta }));
  const best = pickSample(samples);
  if (!best) return null;
  const idOf = (s: TipSample) => s.fields.find(([, v]) => /^[A-Za-z]{1,6}[-_/]\d{3,}$/.test(v))?.[1];
  return { ...best, otherId: samples.map(idOf).find((i) => i && i !== idOf(best)) };
}

export function SearchTips({ onPick }: { onPick: (example: string) => void }) {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const [sample, setSample] = useState<TipSample | null | undefined>(cache.get(tid));
  useEffect(() => {
    if (!tid || cache.has(tid)) return;
    let live = true;
    loadSample(tid).then((s) => { cache.set(tid, s); if (live) setSample(s); }).catch(() => { cache.set(tid, null); });
    return () => { live = false; };
  }, [tid]);
  const tips = useMemo(() => buildTips(sample ?? undefined), [sample]);
  return (
    <div className="flex flex-col gap-3 px-3 pb-2 pt-1">
      {tips.map((g) => (
        <div key={g.title} className="flex flex-col gap-1">
          <Kicker>{g.title}</Kicker>
          <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
            {g.tips.map((t) => (
              <li key={t.example} className="grid grid-cols-[minmax(0,15rem)_minmax(0,1fr)] items-baseline gap-x-4 gap-y-0.5 max-sm:grid-cols-1">
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(t.example)}
                  className="justify-self-start max-w-full truncate bg-n3 hover:bg-accent-soft hover:text-accent border-0 rounded-chip px-2 py-0.5 cursor-pointer font-mono text-small text-n10 text-left">
                  {t.example}
                </button>
                <span className="text-small text-n8">{t.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="m-0 text-micro text-n8 max-w-2xl">{SEARCH_TIPS_NOTE}</p>
    </div>
  );
}
